import { currentSearchAtom } from 'data/Atom/Search';
import { formStore } from 'data/Atom/Store';

import { convertSearchToUrlParams } from 'utility/Convert/Search/searchToUrlParams';
import Console from 'utility/Debug/Console';
import { callApi } from 'utility/Request/callApi';
import { trackGa4Event } from 'utility/Track/trackingHelper';

import { OriginStep, PageType, VerticalType } from 'types/common';
import { SwitchData } from 'types/legacy/backendInformation';
import { ApiEndpoint } from 'types/Request/endpoint';
import { SearchParam } from 'types/Search/params';
import { RedirectSearchParam } from 'types/Search/redirectParams';
import { ServiceDataRequest, ServiceDataResponse } from 'types/serviceData';

import {
    originStepMapping,
    serviceDataEndpointMapping,
    trackingMapping,
    verticalShortcutMapping,
} from '../../../__helper/integratedOvernightRouteMapping';

export const redirectToVertical = async (
    type: VerticalType,
    route: PageType,
    switchData?: SwitchData
) => {
    const redirectUrl = await getRedirectUrl(type, switchData, route);

    if (redirectUrl) {
        trackGa4Event('formToggle_click', {
            linktext: getTrackingMapping(type),
        });

        window.location.href = redirectUrl;
    } else {
        const errorMessage = `getRedirectUrl for ${type} : "${redirectUrl}" - failed`;
        Console.error(errorMessage);
        throw errorMessage;
    }
};

const getOriginStep = (route?: PageType) => {
    if (!route) {
        return OriginStep.Home;
    }

    return originStepMapping[route] ?? OriginStep.Home;
};

const getMappingData = (switchData?: SwitchData) => {
    const mapping = switchData?.mapping ?? {};
    const url: string | undefined = switchData?.originUrl;

    return { url, mapping } ?? {};
};

const getTrackingMapping = (type: VerticalType) => {
    return trackingMapping[type] ?? 'Pauschalreise';
};

const getMappingValue = (params: URLSearchParams, key: string) => {
    if (key === SearchParam.HotelId) {
        const hotelId = params.get(SearchParam.HotelId);

        return hotelId === null
            ? params.get(SearchParam.DirectHotelSearchId)
            : hotelId;
    }

    return params.get(key);
};

const fetchServiceCodeData = async (
    type: VerticalType,
    requestData: ServiceDataRequest
): Promise<ServiceDataResponse | null> => {
    try {
        const endpoint = <
            | ApiEndpoint.ServiceDataHotel
            | ApiEndpoint.ServiceDataCmx
            | ApiEndpoint.ServiceDataHolidayHome
        >serviceDataEndpointMapping[type];
        const response = await callApi(endpoint, {
            requestData,
        });

        return response.parsedBody ?? {};
    } catch (error) {
        throw new Error((error as Record<string, unknown>).message as string);
    }
};

const getRedirectUrl = async (
    type: VerticalType,
    switchData?: SwitchData,
    route?: PageType
) => {
    const mappingData = getMappingData(switchData);

    if (!mappingData?.url) {
        return '';
    }

    const verticalShortcut = verticalShortcutMapping[type];
    const verticalUrl = mappingData.url;
    const [verticalUrlPath, verticalUrlQuery] = verticalUrl.split('?');
    const redirectUrlParams = new URLSearchParams(verticalUrlQuery);
    const currentSearch = formStore.get(currentSearchAtom);
    const currentUrlParams = convertSearchToUrlParams(currentSearch);
    const mandatory = [
        RedirectSearchParam.Origin,
        `${verticalShortcut}SrvCd`,
        RedirectSearchParam.EntryUrl,
    ];
    const pvStepParam = getOriginStep(route);
    redirectUrlParams.set(RedirectSearchParam.Step, pvStepParam);

    mandatory.forEach(function (key) {
        const value = redirectUrlParams.get(key);
        if (value) {
            redirectUrlParams.set(key, value);
        }
    });

    Object.entries(mappingData.mapping).forEach(([key, mappingName]) => {
        const mappingValue = getMappingValue(currentUrlParams, key);

        if (mappingValue && mappingValue !== '-') {
            redirectUrlParams.set(mappingName, mappingValue);
        }
    });

    const serviceCodeData = await fetchServiceCodeData(type, {
        url: `${location.pathname.substring(1)}?${currentUrlParams.toString()}`,
    });

    redirectUrlParams.set(
        RedirectSearchParam.ServiceCode,
        serviceCodeData?.serviceCode ?? ''
    );
    redirectUrlParams.set(
        RedirectSearchParam.SearchChanged,
        `${serviceCodeData?.searchChanged ?? 0}`
    );
    redirectUrlParams.set(RedirectSearchParam.SearchBoxOpen, '1');

    return `${verticalUrlPath}?${redirectUrlParams.toString()}`;
};
